import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import { StatsContainer } from '../../molecules/StatsContainer'
import {
  ChartContainer,
  ChartGrid,
  ChartSection,
  ChartTitle,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import {
  formatPrice,
  formatQuarter,
  getChartColors,
  formatShortPrice,
  processTimeSeriesData,
  calculateKPIs,
  generateInsights,
  convertRentalPrice,
} from './PriceEvolutionChart.utils'
import {
  PriceEvolutionChartProps,
  PriceEvolutionChartRef,
  ChartViewType,
  // KPIData,
} from './PriceEvolutionChart.types'

// Default translation function (returns key as fallback)
const defaultT = (key: string): string => {
  // Extract last part of key as fallback
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

// Anchor the x-axis right edge at the start of the last complete quarter, so the
// chart always reaches "today" even when trailing quarters were filtered out for
// lacking statistically meaningful data.
const getLastCompleteQuarterTimestamp = (): number => {
  const now = new Date()
  const currentQuarter = Math.floor(now.getMonth() / 3)
  let year = now.getFullYear()
  let quarter = currentQuarter - 1
  if (quarter < 0) {
    quarter = 3
    year -= 1
  }
  return new Date(year, quarter * 3, 1).getTime()
}

// Helper to translate property category names
const translateCategory = (category: string, t: (key: string) => string): string => {
  const key = category.toLowerCase()
  if (key === 'apartment') return t('charts.categories.apartment')
  if (key === 'house') return t('charts.categories.house')
  if (key === 'all types' || key === 'all') return t('charts.labels.allTypes')
  return category
}

// View and price type keys for translation
const VIEW_TYPE_KEYS = ['absolute', 'index', 'growth'] as const
const PRICE_TYPE_KEYS = ['median', 'average'] as const

export const PriceEvolutionChart = forwardRef<PriceEvolutionChartRef, PriceEvolutionChartProps>(
  (
    {
      data,
      loading = false,
      error = null,
      currentView: controlledView,
      onViewChange,
      isRental = false,
      isSqm = false,
      priceMode,
      title,
      height = 350,
      showKPIs = true,
      showInsights = true,
      showControls = true,
      showQuantileChart = true,
      t = defaultT,
      priceType: controlledPriceType,
      onPriceTypeChange,
      hidePriceTypeButtons = false,
      showToolbox = true,
    },
    ref
  ) => {
    // Translation helper for price/rent labels
    isRental ? t('charts.labels.rent') : t('charts.labels.price')
    const priceOrRent = isRental ? 'rent' : 'price'
    const [internalView, setInternalView] = useState<ChartViewType['type']>('absolute')
    const currentView = controlledView ?? internalView
    const [internalPriceType, setInternalPriceType] = useState<'median' | 'average'>('median')
    const priceType = controlledPriceType ?? internalPriceType
    const handlePriceTypeChange = (pt: 'median' | 'average') => {
      if (onPriceTypeChange) {
        onPriceTypeChange(pt)
      } else {
        setInternalPriceType(pt)
      }
    }

    const chartRef = useRef<HTMLDivElement>(null)
    const chartInstance = useRef<echarts.ECharts | null>(null)
    const quantileChartRef = useRef<HTMLDivElement>(null)
    const quantileChartInstance = useRef<echarts.ECharts | null>(null)
    const isPrintingRef = useRef(false)

    // Expose export methods via ref
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (chartInstance.current) {
          return chartInstance.current.getDataURL({
            type: 'jpeg',
            pixelRatio: 2,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportQuantileChart: () => {
        if (quantileChartInstance.current) {
          return quantileChartInstance.current.getDataURL({
            type: 'jpeg',
            pixelRatio: 2,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getKPIs: () => kpiData as any,
      getInsights: () => insights,
    }))

    useEffect(() => {
      if (!loading && !error && data && Object.keys(data).length > 0) {
        createChart()
        createQuantileChart()
      }

      return () => {
        chartInstance.current?.dispose()
        quantileChartInstance.current?.dispose()
      }
    }, [data, currentView, priceType, loading, error, isRental, priceMode])

    // Use ResizeObserver to properly resize charts when container size changes
    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        chartInstance.current?.resize()
        quantileChartInstance.current?.resize()
      })

      if (chartRef.current) {
        resizeObserver.observe(chartRef.current)
      }
      if (quantileChartRef.current) {
        resizeObserver.observe(quantileChartRef.current)
      }

      return () => resizeObserver.disconnect()
    }, [])

    // Preserve decimals for rental m² prices (typically CHF 15-35 where decimals matter)
    const preserveDecimals = isRental && isSqm
    const processedData = processTimeSeriesData(data, preserveDecimals)

    // Calculate and set KPIs
    const kpis = calculateKPIs(processedData, priceType)

    // Create growth value with color styling
    const growthValue = (
      <span style={{ color: kpis.overallGrowth < 0 ? '#ff3b30' : '#00b341' }}>
        {`${kpis.overallGrowth >= 0 ? '+' : ''}${kpis.overallGrowth.toFixed(1)}%`}
      </span>
    )

    // Use 2 decimals for rental m² prices
    const kpiFractionDigits = preserveDecimals ? 2 : 0
    const priceTypeLabel = t(`charts.priceTypes.${priceType}`)
    // Convert KPI price for rental mode
    const kpiDisplayPrice = convertRentalPrice(kpis.avgPrice, isRental, priceMode)
    const periodSuffix = isRental ? (priceMode === 'yearly' ? '/y' : '/m') : ''

    const kpiData = [
      {
        id: 'avg-price',
        label: t(`charts.kpis.${priceOrRent}AllTypes`, {
          priceType: priceTypeLabel,
        }),
        value:
          new Intl.NumberFormat('en-CH', {
            style: 'currency',
            currency: 'CHF',
            minimumFractionDigits: kpiFractionDigits,
            maximumFractionDigits: kpiFractionDigits,
          }).format(preserveDecimals ? kpiDisplayPrice : Math.round(kpiDisplayPrice)) +
          (isSqm ? '/m²' : '') +
          periodSuffix,
        variant: 'default',
      },
      {
        id: 'year-growth',
        label: t('charts.kpis.yearGrowth'),
        value: growthValue as any,
        variant: 'default',
      },
      {
        id: 'expensive-category',
        label: t('charts.kpis.mostExpensive'),
        value: kpis.mostExpensiveCategory
          ? translateCategory(kpis.mostExpensiveCategory[0], t)
          : t('charts.labels.notAvailable'),
        variant: 'default',
      },
      {
        id: 'market-volume',
        label: t('charts.kpis.marketVolume'),
        value: kpis.totalVolume.toLocaleString(),
        variant: 'default',
      },
    ]

    // Generate insights
    const insights = generateInsights(processedData, priceType, t)

    const handleViewChange = (view: ChartViewType['type']) => {
      if (onViewChange) {
        onViewChange(view)
      } else {
        setInternalView(view)
      }
    }

    const createChart = () => {
      if (!chartRef.current) return

      if (chartInstance.current) {
        chartInstance.current.dispose()
      }

      chartInstance.current = safeChartInit(chartRef.current, undefined, {
        renderer: 'canvas',
        devicePixelRatio: isPrintingRef.current ? 1 : window.devicePixelRatio || 1,
      })

      if (isPrintingRef.current) {
        setTimeout(() => {
          chartInstance.current?.resize()
        }, 50)
      }

      // Disable animation when rendered inside the print report. An animated
      // ECharts canvas is promoted to a composited layer, which Chrome's print
      // engine wraps in inverting alpha soft masks (/TR {1 exch sub}) that
      // macOS Preview/Quick Look mis-render as white boxes. A static canvas is
      // printed as a plain raster image (matching the Building Permits report,
      // which prints cleanly). See safeChartInit + isPrintMode below.
      const isPrintMode = chartRef.current?.closest('.print-report') !== null
      const option = getChartOption()
      chartInstance.current.setOption({ ...option, animation: !isPrintMode })
    }

    const createQuantileChart = () => {
      if (!quantileChartRef.current) return

      // Check if we have quantile data
      const hasQuantileData = Object.values(processedData).some((categoryData: any) =>
        categoryData.some((d: any) => d.quantiles)
      )

      if (!hasQuantileData) return

      if (quantileChartInstance.current) {
        quantileChartInstance.current.dispose()
      }

      quantileChartInstance.current = safeChartInit(quantileChartRef.current, undefined, {
        renderer: 'canvas',
        devicePixelRatio: isPrintingRef.current ? 1 : window.devicePixelRatio || 1,
      })

      if (isPrintingRef.current) {
        setTimeout(() => {
          quantileChartInstance.current?.resize()
        }, 50)
      }

      const isPrintMode = quantileChartRef.current?.closest('.print-report') !== null
      const option = createQuantileOption()
      quantileChartInstance.current.setOption({
        ...option,
        animation: !isPrintMode,
      })
    }

    const getChartOption = () => {
      switch (currentView) {
        case 'index':
          return createPriceIndexOption()
        case 'growth':
          return createGrowthRateOption()
        default:
          return createAbsolutePriceOption()
      }
    }

    const createQuantileOption = () => {
      // const colors = getChartColors()

      // Get all unique dates from all categories
      const allDates = new Set<string>()
      Object.values(processedData).forEach((categoryData) => {
        categoryData.forEach((d) => allDates.add(d.date))
      })
      const sortedDates = Array.from(allDates).sort()

      // Prepare data for all categories combined (for now we'll show all property types combined)
      const combinedData: any[] = []
      sortedDates.forEach((date) => {
        let q10Total = 0,
          q30Total = 0,
          q50Total = 0,
          q70Total = 0,
          q90Total = 0
        let count = 0

        Object.values(processedData).forEach((categoryData) => {
          const dataPoint = categoryData.find((d) => d.date === date)
          if (dataPoint && dataPoint.quantiles) {
            q10Total += dataPoint.quantiles.q10
            q30Total += dataPoint.quantiles.q30
            q50Total += dataPoint.quantiles.q50
            q70Total += dataPoint.quantiles.q70
            q90Total += dataPoint.quantiles.q90
            count++
          }
        })

        if (count > 0) {
          combinedData.push({
            date,
            q10: q10Total / count,
            q30: q30Total / count,
            q50: q50Total / count,
            q70: q70Total / count,
            q90: q90Total / count,
          })
        }
      })

      const formatDate = (date: string) => formatQuarter(date)

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          appendTo: () => document.body,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const date = params[0].axisValue
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${formatDate(date)}</div>`
            html += '<div style="display: grid; gap: 4px;">'

            params.forEach((param: any) => {
              html += `
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; border-radius: 2px;"></span>
                <span style="flex: 1;">${param.seriesName}:</span>
                <span style="font-weight: 600;">${formatPrice(param.value, isRental, isSqm, priceMode)}</span>
              </div>
            `
            })

            html += '</div>'
            return html
          },
        },
        legend: {
          data: [
            t('charts.labels.percentile10'),
            t('charts.labels.percentile30'),
            t('charts.labels.percentile50Median'),
            t('charts.labels.percentile70'),
            t('charts.labels.percentile90'),
          ],
          bottom: 20,
          left: 'center',
          textStyle: { fontSize: 11 },
        },
        grid: {
          left: '10%',
          right: '5%',
          top: 30,
          bottom: 80,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: combinedData.map((d) => d.date),
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
            fontSize: 10,
            rotate: 45,
            formatter: formatDate,
            interval: Math.max(Math.floor(combinedData.length / 10), 1) - 1,
          },
        },
        yAxis: {
          type: 'value',
          name: '',
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            color: '#6c757d',
            formatter: (value: number) => formatShortPrice(value, isRental, false, priceMode),
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: t('charts.labels.percentile10'),
            type: 'bar',
            data: combinedData.map((d) => d.q10 || 0),
            itemStyle: { color: '#dbeafe' }, // Light blue
            barCategoryGap: '10%',
            barGap: '0%',
          },
          {
            name: t('charts.labels.percentile30'),
            type: 'bar',
            data: combinedData.map((d) => d.q30 || 0),
            itemStyle: { color: '#93c5fd' }, // Medium blue
            barCategoryGap: '10%',
            barGap: '0%',
          },
          {
            name: t('charts.labels.percentile50Median'),
            type: 'bar',
            data: combinedData.map((d) => d.q50 || 0),
            itemStyle: { color: '#3b82f6' }, // Blue
            barCategoryGap: '10%',
            barGap: '0%',
          },
          {
            name: t('charts.labels.percentile70'),
            type: 'bar',
            data: combinedData.map((d) => d.q70 || 0),
            itemStyle: { color: '#1d4ed8' }, // Dark blue
            barCategoryGap: '10%',
            barGap: '0%',
          },
          {
            name: t('charts.labels.percentile90'),
            type: 'bar',
            data: combinedData.map((d) => d.q90 || 0),
            itemStyle: { color: '#1e3a8a' }, // Very dark blue
            barCategoryGap: '10%',
            barGap: '0%',
          },
        ],
      }
    }

    const createAbsolutePriceOption = () => {
      const colors = getChartColors()
      const series = Object.entries(processedData).map(([category, values]) => ({
        name: translateCategory(category, t),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 3 },
        itemStyle: {
          color: colors[category.toLowerCase()] || '#6c757d',
        },
        data: values.map((v) => [v.date, priceType === 'median' ? v.medianPrice : v.avgPrice]),
        emphasis: {
          focus: 'series',
          lineStyle: { width: 4 },
        },
      }))

      return {
        tooltip: {
          trigger: 'axis',
          appendTo: () => document.body,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            const firstParam = params[0]
            const quarter = formatQuarter(firstParam.axisValue)
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${quarter}</div>`

            params.forEach((param: any) => {
              html += `
              <div style="display: flex; align-items: center; margin: 4px 0;">
                <span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                <span style="flex: 1;">${param.seriesName}:</span>
                <span style="font-weight: 600;">${formatPrice(param.value[1], isRental, isSqm, priceMode)}</span>
              </div>
            `
            })
            return html
          },
        },
        legend: {
          data: series.map((s) => s.name),
          bottom: 0,
          left: 'center',
          type: 'scroll',
          textStyle: { fontSize: 12 },
          itemGap: 16,
          itemWidth: 20,
          itemHeight: 10,
          pageButtonItemGap: 5,
          pageIconSize: 12,
        },
        grid: {
          left: 10,
          right: 10,
          top: 20,
          bottom: 50,
          containLabel: true,
        },
        xAxis: {
          type: 'time',
          boundaryGap: false,
          max: getLastCompleteQuarterTimestamp(),
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
            formatter: (value: number) => formatQuarter(new Date(value).toISOString()),
          },
        },
        yAxis: {
          type: 'value',
          scale: true,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
            formatter: (value: number) => {
              return (
                'CHF ' + formatShortPrice(value, isRental, false, priceMode) + (isSqm ? '/m²' : '')
              )
            },
          },
          splitLine: {
            lineStyle: { color: '#f0f0f0' },
          },
        },
        series,
        toolbox: showToolbox
          ? {
              show: true,
              feature: {
                saveAsImage: {
                  show: true,
                  title: t('charts.actions.saveAsImage'),
                  excludeComponents: ['toolbox', 'dataZoom'],
                },
              },
            }
          : { show: false },
      }
    }

    const createPriceIndexOption = () => {
      const colors = getChartColors()
      const series = Object.entries(processedData).map(([category, values]) => {
        const basePrice =
          priceType === 'median' ? values[0]?.medianPrice || 100 : values[0]?.avgPrice || 100
        return {
          name: translateCategory(category, t),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 3 },
          itemStyle: {
            color: colors[category.toLowerCase()] || '#6c757d',
          },
          data: values.map((v) => [
            v.date,
            Number(
              (((priceType === 'median' ? v.medianPrice : v.avgPrice) / basePrice) * 100).toFixed(2)
            ),
          ]),
          emphasis: {
            focus: 'series',
            lineStyle: { width: 4 },
          },
        }
      })

      const baseOption = createAbsolutePriceOption()
      return {
        ...baseOption,
        series,
        yAxis: {
          ...baseOption.yAxis,
          axisLabel: {
            color: '#6c757d',
            formatter: '{value}',
          },
        },
        tooltip: {
          ...baseOption.tooltip,
          formatter: (params: any) => {
            const firstParam = params[0]
            const quarter = formatQuarter(firstParam.axisValue)
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${quarter}</div>`

            params.forEach((param: any) => {
              const change = param.value[1] - 100
              const changeColor = change >= 0 ? '#10b981' : '#ef4444'
              html += `
              <div style="display: flex; align-items: center; margin: 4px 0;">
                <span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                <span style="flex: 1;">${param.seriesName}:</span>
                <span style="font-weight: 600;">${param.value[1]}</span>
                <span style="color: ${changeColor}; margin-left: 8px;">(${change >= 0 ? '+' : ''}${change.toFixed(1)}%)</span>
              </div>
            `
            })
            return html
          },
        },
      }
    }

    const createGrowthRateOption = () => {
      const colors = getChartColors()
      const series = Object.entries(processedData).map(([category, values]) => {
        const growthData = values
          .map((v, i) => {
            if (i < 4) return null
            const prevYearValue = values[i - 4]
            const prevPrice =
              priceType === 'median' ? prevYearValue.medianPrice : prevYearValue.avgPrice
            const currentPrice = priceType === 'median' ? v.medianPrice : v.avgPrice
            if (!prevYearValue || prevPrice === 0) return null
            const growth = ((currentPrice - prevPrice) / prevPrice) * 100
            return [v.date, Number(growth.toFixed(2))]
          })
          .filter((v) => v !== null)

        return {
          name: translateCategory(category, t),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 3 },
          itemStyle: {
            color: colors[category.toLowerCase()] || '#6c757d',
          },
          data: growthData,
          emphasis: {
            focus: 'series',
            lineStyle: { width: 4 },
          },
        }
      })

      const baseOption = createAbsolutePriceOption()
      return {
        ...baseOption,
        series,
        yAxis: {
          ...baseOption.yAxis,
          axisLabel: {
            color: '#6c757d',
            formatter: '{value}%',
          },
        },
        tooltip: {
          ...baseOption.tooltip,
          formatter: (params: any) => {
            const firstParam = params[0]
            const quarter = formatQuarter(firstParam.axisValue)
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${quarter}</div>`

            params.forEach((param: any) => {
              const value = param.value[1]
              const color = value >= 0 ? '#10b981' : '#ef4444'
              html += `
              <div style="display: flex; align-items: center; margin: 4px 0;">
                <span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                <span style="flex: 1;">${param.seriesName}:</span>
                <span style="font-weight: 600; color: ${color};">${value >= 0 ? '+' : ''}${value}%</span>
              </div>
            `
            })
            return html
          },
        },
      }
    }

    useEffect(() => {
      // Match the Building Permits report, which prints cleanly: do NOT touch
      // the charts on print. The previous beforeprint/afterprint/matchMedia
      // handlers called chart.resize() + setOption() *during* the print
      // snapshot, which made Chrome serialise the canvas as a masked vector
      // figure (the white boxes in macOS Preview / Quick Look). Building
      // Permits charts do nothing on print and rasterise cleanly. The toolbox
      // is already hidden at creation (showToolbox=false in the report) and the
      // print layout keeps the same width, so no print-time resize is needed.
      const handleResize = () => {
        chartInstance.current?.resize()
        quantileChartInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            {t('charts.states.loading')}
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    if (Object.keys(processedData).length === 0) {
      return (
        <NotAvailable
          title={t('charts.empty.title')}
          description={t(`charts.empty.${priceOrRent}Evolution`)}
        />
      )
    }

    const chartTitle = title || t(`charts.titles.${priceOrRent}EvolutionByType`)

    return (
      <PrintWrapper>
        {showControls && !hidePriceTypeButtons && (
          <Box marginBottom="12px" marginTop="12px">
            <Box display="flex" justifyContent="flex-end">
              <ButtonGroup>
                {PRICE_TYPE_KEYS.map((pt) => (
                  <Button
                    key={pt}
                    variant={priceType === pt ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => handlePriceTypeChange(pt)}
                  >
                    {t(`charts.priceTypes.${pt}`)}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>
        )}
        {showKPIs && kpiData.length > 0 && (
          <Box marginBottom="16px">
            <StatsContainer fullWidth stats={kpiData as any} />
          </Box>
        )}

        {/* Main Chart and Quantile Chart in 2-column grid */}
        {showQuantileChart &&
        Object.values(processedData).some((categoryData: any) =>
          categoryData.some((d: any) => d.quantiles)
        ) ? (
          <ChartGrid columns={2}>
            <ChartSection>
              <Box marginBottom="16px">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap="12px"
                >
                  <ChartTitle>{chartTitle}</ChartTitle>
                  {showControls && (
                    <ButtonGroup>
                      {VIEW_TYPE_KEYS.map((viewKey) => (
                        <Button
                          key={viewKey}
                          variant={currentView === viewKey ? 'primary' : 'secondary'}
                          size="small"
                          onClick={() => handleViewChange(viewKey)}
                          style={{
                            padding: '2px 6px',
                            fontSize: '10px',
                            lineHeight: '1.2',
                            minHeight: 'unset',
                          }}
                        >
                          {t(`charts.views.${viewKey}`)}
                        </Button>
                      ))}
                    </ButtonGroup>
                  )}
                </Box>
              </Box>
              <ChartContainer height={height} ref={chartRef} />
            </ChartSection>
            <ChartSection
              title={
                t(`charts.titles.${priceOrRent}DistributionByQuantiles`) + (isSqm ? '/m²' : '')
              }
            >
              <ChartContainer height={height} ref={quantileChartRef} />
            </ChartSection>
          </ChartGrid>
        ) : (
          <ChartSection>
            <Box marginBottom="12px">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap="12px"
              >
                <ChartTitle>{chartTitle}</ChartTitle>
                {showControls && (
                  <Box display="flex" gap="16px" flexWrap="wrap">
                    <ButtonGroup>
                      {VIEW_TYPE_KEYS.map((viewKey) => (
                        <Button
                          key={viewKey}
                          variant={currentView === viewKey ? 'primary' : 'secondary'}
                          size="small"
                          onClick={() => handleViewChange(viewKey)}
                        >
                          {t(`charts.views.${viewKey}`)}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Box>
                )}
              </Box>
            </Box>
            <ChartContainer height={height} ref={chartRef} />
          </ChartSection>
        )}

        {showInsights && insights.length > 0 && (
          <Box marginTop="16px">
            <ChartTitle style={{ marginBottom: '12px' }}>
              {t('charts.insights.keyInsights')}
            </ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="12px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 280px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '12px',
                    minWidth: 0,
                  }}
                >
                  <Text
                    variant="h5"
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '4px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '11px',
                      lineHeight: 1.4,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

PriceEvolutionChart.displayName = 'PriceEvolutionChart'
